import { analytics } from '../../utils/analytics.js';
import type { Power } from './engine/world.js';

function byId<T extends HTMLElement>(id: string, type: new () => T): T {
  const element = document.getElementById(id);
  if (!(element instanceof type)) throw new Error(`Forest Runner: missing #${id}`);
  return element;
}

function powerChip(power: Power): HTMLElement {
  const element = document.querySelector(`[data-power="${power}"]`);
  if (!(element instanceof HTMLElement)) throw new Error(`Forest Runner: missing ${power} chip`);
  return element;
}

document.addEventListener('DOMContentLoaded', async () => {
  analytics.trackPageView('forest-runner');
  const play = byId('play', HTMLElement);
  const canvas = byId('view', HTMLCanvasElement);

  // Three.js is loaded after the page has painted, so the guide and the top bar never wait for it.
  const { TrailView, webglAvailable, loadForest, loadFox } = await import('./ui/TrailView.js');
  if (!webglAvailable()) {
    play.classList.add('is-unsupported');
    byId('overlay-title', HTMLElement).textContent = 'This browser cannot draw the forest';
    byId('overlay-text', HTMLElement).textContent = 'Forest Runner needs WebGL, which is turned off or missing here. Try another browser or device.';
    byId('legend', HTMLElement).hidden = true;
    return;
  }
  // The models load alongside the game code. If they cannot load, the run still works,
  // on open grass with the built-in fox.
  const [{ ForestRunnerGame }, forest, fox] = await Promise.all([
    import('./game/ForestRunnerGame.js'),
    loadForest().catch((error: unknown) => {
      console.warn('Forest Runner: could not load the forest', error);
      return null;
    }),
    loadFox().catch((error: unknown) => {
      console.warn('Forest Runner: could not load the fox', error);
      return null;
    }),
  ]);

  const elements = {
    stage: byId('stage', HTMLElement),
    play,
    canvas,
    score: byId('score', HTMLElement),
    best: byId('best-score', HTMLElement),
    coins: byId('coins', HTMLElement),
    bestStat: byId('best-stat', HTMLElement),
    pause: byId('pause', HTMLButtonElement),
    overlay: byId('overlay', HTMLElement),
    overlayTitle: byId('overlay-title', HTMLElement),
    overlayText: byId('overlay-text', HTMLElement),
    legend: byId('legend', HTMLElement),
    actions: byId('overlay-actions', HTMLElement),
    resume: byId('resume', HTMLButtonElement),
    restart: byId('restart', HTMLButtonElement),
    switches: byId('switches', HTMLElement),
    sound: byId('sound', HTMLButtonElement),
    music: byId('music', HTMLButtonElement),
    count: byId('count', HTMLElement),
    paceButtons: [...document.querySelectorAll('[data-pace]')].filter((el): el is HTMLButtonElement => el instanceof HTMLButtonElement),
    powers: {
      magnet: powerChip('magnet'),
      boost: powerChip('boost'),
      double: powerChip('double'),
      shield: powerChip('shield'),
      sprint: powerChip('sprint'),
    },
    badge: byId('badge', HTMLElement),
    zone: byId('zone', HTMLElement),
    ring: byId('ring', HTMLElement),
    ringPoints: byId('ring-points', HTMLElement),
    ringStreak: byId('ring-streak', HTMLElement),
    hint: byId('hint', HTMLElement),
    announcer: byId('announcer', HTMLElement),
    shop: {
      open: byId('shop-open', HTMLButtonElement),
      openFox: byId('shop-open-fox', HTMLElement),
      openCoins: byId('shop-open-coins', HTMLElement),
      panel: byId('shop', HTMLElement),
      back: byId('shop-back', HTMLButtonElement),
      close: byId('shop-close', HTMLButtonElement),
      coins: byId('shop-coins', HTMLElement),
      grid: byId('shop-grid', HTMLElement),
      detail: byId('shop-detail', HTMLElement),
      portrait: byId('shop-portrait', HTMLElement),
      name: byId('shop-name', HTMLElement),
      blurb: byId('shop-blurb', HTMLElement),
      have: byId('shop-have', HTMLElement),
      price: byId('shop-price', HTMLElement),
      totalLabel: byId('shop-total-label', HTMLElement),
      total: byId('shop-total', HTMLElement),
      buy: byId('shop-buy', HTMLButtonElement),
      cancel: byId('shop-cancel', HTMLButtonElement),
    },
  };
  let game: InstanceType<typeof ForestRunnerGame> | null = null;
  const view = new TrailView(canvas, () => game?.contextLost(), forest, fox);
  game = new ForestRunnerGame(elements, view);
  play.classList.add('is-ready');
});
